import {isDisabled} from '@/library/dom';
import {type Directive, type DirectiveBinding} from 'vue';

function getEvents(directiveOptions: DirectiveBinding): VInteractEventCallbacks
{
    const eventHandler: (event: KeyboardEvent | MouseEvent | TouchEvent) => void = directiveOptions.value;

    return {
        click: (event) =>
        {
            directiveOptions.modifiers.stop && event.stopPropagation();
            event.preventDefault();
            !event.altKey && eventHandler(event);
        },
        touchstart: (event: TouchEvent) =>
        {
            directiveOptions.modifiers.stop && event.stopPropagation();
            event.preventDefault();
            !event.altKey && eventHandler(event);
        },
        keydown: (event) => {
            directiveOptions.modifiers.stop && event.stopPropagation();
            'Space' === event.code && event.preventDefault()
        },
        keyup: (event) => {
            directiveOptions.modifiers.stop && event.stopPropagation();
            keyupHandler(event, eventHandler, directiveOptions)
        },
    };
}

function addEvents(element: HTMLElement, events: VInteractEventCallbacks)
{
    element.addEventListener('click', events.click);
    element.addEventListener('touchstart', events.touchstart, { passive: true });
    element.addEventListener('keydown', events.keydown);
    element.addEventListener('keyup', events.keyup);
    element._vPortunaInteract = events;
}

function removeEvents(element: HTMLElement)
{
    if (element._vPortunaInteract)
    {
        const events = element._vPortunaInteract;
        element.removeEventListener('click', events.click);
        element.removeEventListener('touchstart', events.touchstart);
        element.removeEventListener('keydown', events.keydown);
        element.removeEventListener('keyup', events.keyup);
        element._vPortunaInteract = null;
    }
}

function mounted(element: HTMLElement, directiveOptions: DirectiveBinding)
{
    addEvents(element, getEvents(directiveOptions));
    handleAttributes(element, directiveOptions);
}

function updated(element: HTMLElement, directiveOptions: DirectiveBinding)
{
    removeEvents(element);
    addEvents(element, getEvents(directiveOptions));

    handleAttributes(element, directiveOptions);
}

function handleAttributes(element: HTMLElement, directiveOptions: DirectiveBinding)
{
    if (!isDisabled(element))
    {
        element.classList.add('pointer');

        if ('A' === element.tagName)
        {
            !element.getAttribute('href') && element.setAttribute('href', 'javascript:void(0);');
        }
        else if (!directiveOptions.modifiers.noindex)
        {
            if (!element.getAttribute('tabindex'))
            {
                element.setAttribute('tabindex', '0');
            }

            if(!element.getAttribute('role'))
            {
                element.setAttribute('role', 'button');
            }
        }
    }
    else
    {
        element.classList.remove('pointer');

        if (!directiveOptions.modifiers.noindex)
        {
            element.removeAttribute('tabindex');
        }
    }
}

function keyupHandler(event: KeyboardEvent, eventHandler: (event: KeyboardEvent|MouseEvent) => void, directiveOptions: DirectiveBinding)
{
    let keyCodes = ['Enter','NumpadEnter'];

    if(!directiveOptions.modifiers.link)
    {
        keyCodes.push('Space');
    }

    if(keyCodes.includes(event.code))
    {
        event.preventDefault();
        eventHandler(event);
    }
}

const interact: Directive = {
    mounted,
    updated,
};

export default interact;